import React, { useState } from 'react';
import {
  Box,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
  Divider,
  Avatar,
} from '@mui/material';
import {
  Store,
  Casino,
  Assessment,
  Logout,
  Email,
  Group,
  Memory,
  ReportProblemOutlined,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { isSuperAdmin } from '../../utils/auth/currentUser';
import { adminColors, adminTypography } from '../../styles/adminDesignTokens';
import {
  adminAppShellSx,
  adminCombineSx,
  adminDividerSx,
  adminMainContentInnerSx,
  adminMainContentSx,
  adminSidebarAvatarSx,
  adminSidebarHeaderSx,
  adminSidebarNavItemSx,
  adminSidebarPaperSx,
  adminSidebarSectionLabelSx,
  adminSidebarUserActionSx,
} from '../../styles/adminComponentStyles';

interface DashboardLayoutProps {
  children: React.ReactNode;
}

const DRAWER_WIDTH = 220;

type DashboardMenuItem = {
  text: string;
  icon: React.ReactNode;
  path: string;
  children?: DashboardMenuItem[];
};

const nestedSidebarItemSx = {
  pl: 4,
  position: 'relative',
  '&::before': {
    borderLeft: `1px solid ${adminColors.borderHover}`,
    content: '""',
    height: 18,
    left: 30,
    position: 'absolute',
    top: 0,
  },
  '&::after': {
    borderTop: `1px solid ${adminColors.borderHover}`,
    content: '""',
    left: 30,
    position: 'absolute',
    top: 18,
    width: 16,
  },
} as const;

const nestedSidebarNavItemSx = adminCombineSx(adminSidebarNavItemSx, {
  minHeight: 30,
  pl: 2,
  position: 'relative',
  '&::before': {
    borderRight: `1px solid ${adminColors.borderHover}`,
    borderTop: `1px solid ${adminColors.borderHover}`,
    content: '""',
    height: 6,
    left: 3,
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%) rotate(45deg)',
    width: 6,
  },
  '&.Mui-selected': {
    backgroundColor: 'transparent',
    color: adminColors.primary,
    '&:hover': {
      backgroundColor: 'transparent',
    },
    '& .MuiListItemIcon-root': {
      color: adminColors.primary,
    },
    '& .MuiListItemText-primary': {
      color: adminColors.primary,
    },
  },
});

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  const storeScopedMenuItems = [
    {
      text: 'Stores',
      icon: <Store />,
      path: '/dashboard/store-summary',
    },
  ];

  const systemWideMenuItems = [
    ...(isSuperAdmin() ? [{
      text: 'New Agents',
      icon: <Memory />,
      path: '/dashboard/new-agents',
    }] : []),
    {
      text: 'Audit',
      icon: <Assessment />,
      path: '/dashboard/audit',
    },
    {
      text: 'Users',
      icon: <Group />,
      path: '/dashboard/account-holders',
    },
    {
      text: 'Pipeline Status',
      icon: <Email />,
      path: '/dashboard/email-pipeline',
    },
    {
      text: 'Manage Games',
      icon: <Casino />,
      path: '/dashboard/games',
      children: [
        {
          text: 'Missing Games',
          icon: <ReportProblemOutlined />,
          path: '/dashboard/games/missing',
        },
      ],
    },
  ];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActiveRoute = (path: string) => {
    return location.pathname.includes(path);
  };

  const userData = JSON.parse(localStorage.getItem('userDetails') || '{}');
  const userName = userData.name || '';

  const renderMenuSection = (label: string, items: DashboardMenuItem[]) => (
    <>
      <Typography variant="caption" sx={adminSidebarSectionLabelSx}>
        {label}
      </Typography>
      {items.map((item) => (
        <React.Fragment key={item.text}>
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => handleNavigation(item.path)}
              selected={item.children?.length ? location.pathname === item.path : isActiveRoute(item.path)}
              sx={adminSidebarNavItemSx}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
          {item.children?.map((child) => (
            <ListItem key={child.text} disablePadding sx={nestedSidebarItemSx}>
              <ListItemButton
                onClick={() => handleNavigation(child.path)}
                selected={location.pathname === child.path}
                sx={nestedSidebarNavItemSx}
              >
                <ListItemText primary={child.text} />
              </ListItemButton>
            </ListItem>
          ))}
        </React.Fragment>
      ))}
    </>
  );

  const drawer = (
    <Box sx={{ backgroundColor: adminColors.surface, display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={adminSidebarHeaderSx}>
        <img src="/logo.png" alt="LottoReco Admin Logo" style={{ width: '100%', maxWidth: '116px', height: 'auto' }} />
      </Box>

      <List sx={{ flexGrow: 1, pt: 1 }}>
        {renderMenuSection('Store', storeScopedMenuItems)}
        <Divider sx={adminCombineSx(adminDividerSx, { my: 1, mx: 1.5 })} />
        {renderMenuSection('System Wide', systemWideMenuItems)}
      </List>

      <Box sx={{ p: 0.5 }}>
        <Box
          onClick={handleLogout}
          sx={adminSidebarUserActionSx}
        >
          <Avatar sx={adminSidebarAvatarSx}>
            {userName ? userName.charAt(0).toUpperCase() : 'U'}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{
                ...adminTypography.bodyStrong,
                color: adminColors.text,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                marginBottom: '2px',
              }}
            >
              {userName}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                color: adminColors.danger,
              }}
            >
              <Logout sx={{ fontSize: 16 }} />
              <Typography
                variant="caption"
                sx={{
                  ...adminTypography.helper,
                  color: adminColors.danger,
                }}
              >
                Logout
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={adminAppShellSx}>
      <Box
        component="nav"
        sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              ...adminSidebarPaperSx,
              width: DRAWER_WIDTH,
            },
          }}
        >
          {drawer}
        </Drawer>

        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              ...adminSidebarPaperSx,
              width: DRAWER_WIDTH,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={adminCombineSx(adminMainContentSx, { width: { md: `calc(100% - ${DRAWER_WIDTH}px)` } })}
      >
        <Box sx={adminMainContentInnerSx}>
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default DashboardLayout;
